// DESIGN-SPEC §5.8 StickyBuyBar 吸底购买栏
"use client";

import { Headphones, UserRound } from "lucide-react";
import Link from "next/link";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./button";

const buyBarShadow: CSSProperties = {
  boxShadow:
    "0 -4px 16px color-mix(in srgb, var(--c-forest-900) 8%, transparent)",
};

export function StickyBuyBar({
  sentinelRef,
  buyLabel,
  buyHref,
  leftActions,
  demo = false,
  className,
}: {
  sentinelRef?: RefObject<Element | null>;
  buyLabel: string;
  buyHref?: string;
  leftActions?: ReactNode;
  demo?: boolean;
  className?: string;
}) {
  const [visible, setVisible] = useState(demo || !sentinelRef);

  useEffect(() => {
    if (demo || !sentinelRef) {
      setVisible(true);
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(([entry]) => {
      setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [demo, sentinelRef]);

  return (
    <aside
      aria-label="购买操作"
      className={cn(
        "z-50 border-t border-line bg-card pb-[env(safe-area-inset-bottom)] motion-safe:transition-transform motion-safe:duration-[240ms] motion-safe:ease-out",
        demo
          ? "relative w-full translate-y-0"
          : "responsive-buy-bar fixed bottom-0 left-1/2 w-full -translate-x-1/2",
        !demo && (visible ? "translate-y-0" : "translate-y-full"),
        className,
      )}
      style={buyBarShadow}
    >
      <div className="flex h-14 items-center gap-2 px-2.5">
        {leftActions ?? (
          <div className="flex shrink-0">
            <a
              href="#consult"
              className="flex size-11 flex-col items-center justify-center gap-px text-[10px] leading-3 text-ink-2 focus-visible:outline-2 focus-visible:outline-forest-500"
            >
              <Headphones className="size-5 stroke-[1.5]" aria-hidden="true" />
              客服
            </a>
            <Link
              href="/member"
              className="flex size-11 flex-col items-center justify-center gap-px text-[10px] leading-3 text-ink-2 focus-visible:outline-2 focus-visible:outline-forest-500"
            >
              <UserRound className="size-5 stroke-[1.5]" aria-hidden="true" />
              会员
            </Link>
          </div>
        )}
        <Button
          variant="buy"
          href={buyHref}
          className="min-w-0 flex-1 px-3"
        >
          {buyLabel}
        </Button>
      </div>
    </aside>
  );
}
